import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

import { getAvailableAlphaSenders } from '../settings.service';

export const availableAlphaSendersKeys = {
  list: (countryCode: string, searchTerm: string) =>
    ['alphaSenders', 'available', countryCode, searchTerm] as const,
};

export const AVAILABLE_ALPHA_SENDERS_PAGE_SIZE = 20;

type Options = {
  enabled?: boolean;
  debounceMs?: number;
};

export const useAvailableAlphaSenders = (
  countryCode: string,
  options?: Options
) => {
  const { enabled = true, debounceMs = 300 } = options ?? {};
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), debounceMs),
    [debounceMs]
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  const query = useQuery({
    enabled: enabled && !!countryCode,
    queryFn: () =>
      getAvailableAlphaSenders({
        countryCode,
        senderIdSearchTerm: searchTerm || undefined,
        pageSize: AVAILABLE_ALPHA_SENDERS_PAGE_SIZE,
      }),
    queryKey: availableAlphaSendersKeys.list(countryCode, searchTerm),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    setSearchTerm: debouncedSetSearchTerm,
  };
};
